import { contaFiltri } from './Functions'
import { Icona } from './Icon'
import { FiltriRun } from './RunFilters'
import { TabellaRun } from './RunsTable'
import { t } from './testi'

/** Righe grigie al posto della tabella mentre le run si caricano la prima volta. */
const ScheletroElenco = () => (
  <div className="ae-skel-gruppo ae-vuoto" role="status" aria-label={t.caricamento}>
    {[0, 1, 2, 3, 4].map((i) => (
      <span key={i} className="ae-skel ae-skel--riga" />
    ))}
  </div>
)

/** Lo stato vuoto: icona, frase, un aiuto e (se serve) l'azione che porta avanti. */
const StatoVuoto = ({ icona, titolo, aiuto, azione, ruolo }) => (
  <div className="ae-stato-vuoto" role={ruolo}>
    <Icona nome={icona} />
    <p className="ae-stato-vuoto__titolo">{titolo}</p>
    {aiuto ? <p className="ae-muto">{aiuto}</p> : null}
    {azione}
  </div>
)

/**
 * La card «Run»: filtri, tabella e paginazione, con i quattro stati (caricamento, errore,
 * nessuna run, nessuna run con questi filtri) che si distinguono a colpo d'occhio.
 * @param {Object} props - Proprietà
 * @param {Object} props.query - Il risultato di `useRuns`
 * @param {Array<Object>} props.runs - Le run della pagina
 * @param {Object} props.filtri - I filtri attivi
 * @param {number} props.epocaFiltri - Cambia a ogni azzeramento: svuota il campo di ricerca
 * @param {(campo: string, valore: string) => void} props.onFiltro - Cambia un filtro
 * @param {() => void} props.onAzzeraFiltri - Azzera i filtri
 * @param {Array<Object>} props.environments - Gli environment
 * @param {() => void} props.onNuovaRun - Apre il form di lancio
 * @param {(pagina: number) => void} props.onPagina - Cambia pagina
 * @param {Object} props.tabella - Le proprietà di `TabellaRun` (scelta, azioni)
 */
export const ElencoRun = ({
  query,
  runs,
  filtri,
  epocaFiltri,
  onFiltro,
  onAzzeraFiltri,
  environments,
  onNuovaRun,
  onPagina,
  tabella
}) => {
  const dati = query.data
  const pagina = dati ?? { totale: 0, pagina: 1, pagine: 1 }
  const filtrata = contaFiltri(filtri) > 0
  return (
    <div className="ncard ae-runs-card">
      <div className="ncard__head">
        <div>
          <h3 className="ncard__title">{t.aeRun}</h3>
          <div className="ncard__sub">
            {t.aeRunSotto}
            {dati ? ` · ${pagina.totale} ${t.aeRunTrovate}` : ''}
          </div>
        </div>
      </div>
      <FiltriRun
        key={epocaFiltri}
        filtri={filtri}
        onChange={onFiltro}
        onAzzera={onAzzeraFiltri}
        environments={environments}
        flussi={dati?.flussi ?? []}
        versioni={dati?.versioniHarness ?? []}
      />
      <div className="ncard__body ncard__body--flush">
        {query.isError ? (
          <StatoVuoto
            ruolo="alert"
            icona="pi-exclamation-triangle"
            titolo={t.aeElencoNonLetto}
            aiuto={query.error?.message || t.aeElencoNonLettoAiuto}
            azione={
              <button
                type="button"
                className="nbtn nbtn--ghost nbtn--sm"
                onClick={() => query.refetch()}
              >
                <Icona nome="pi-refresh" /> {t.aeRiprova}
              </button>
            }
          />
        ) : query.isLoading ? (
          <ScheletroElenco />
        ) : !runs.length && filtrata ? (
          <StatoVuoto
            icona="pi-filter"
            titolo={t.aeNessunaFiltrata}
            azione={
              <button type="button" className="nbtn nbtn--ghost nbtn--sm" onClick={onAzzeraFiltri}>
                <Icona nome="pi-filter-slash" /> {t.aeAzzeraFiltri}
              </button>
            }
          />
        ) : !runs.length ? (
          <StatoVuoto
            icona="pi-inbox"
            titolo={t.aeNessunaRun}
            aiuto={t.aeNessunaRunAiuto}
            azione={
              <button type="button" className="nbtn nbtn--primary nbtn--sm" onClick={onNuovaRun}>
                <Icona nome="pi-plus" /> {t.aeLanciaPrima}
              </button>
            }
          />
        ) : (
          <TabellaRun runs={runs} environments={environments} {...tabella} />
        )}
        {pagina.pagine > 1 ? (
          <nav className="ntable-paginator" aria-label={t.aeRun}>
            <span>
              {pagina.totale} · {pagina.pagina}/{pagina.pagine}
            </span>
            <span className="ntable-paginator__pages">
              <button
                type="button"
                className="ntable-paginator__page"
                disabled={pagina.pagina <= 1}
                aria-label={t.aePaginaPrima}
                onClick={() => onPagina(pagina.pagina - 1)}
              >
                <Icona nome="pi-angle-left" />
              </button>
              <button
                type="button"
                className="ntable-paginator__page"
                disabled={pagina.pagina >= pagina.pagine}
                aria-label={t.aePaginaDopo}
                onClick={() => onPagina(pagina.pagina + 1)}
              >
                <Icona nome="pi-angle-right" />
              </button>
            </span>
          </nav>
        ) : null}
      </div>
    </div>
  )
}
